import Notify from 'notifyjs'
import { observable, runInAction } from 'mobx'
import { localStorage as storage } from '@/infra/storage'
import { stripMarkdown } from '@/utils/markdown-util'
import { type Observable, NEVER } from 'rxjs'
import { type User } from '@/features/iam/users/User'
import type { SystemNotification } from '@/features/notifications'
import taxfyleIcon256pxUrl from '@/assets/misc/img_taxfyle-icon-256px.svg'

const DEFAULT_ICON = taxfyleIcon256pxUrl

/**
 * Provides the ability to show desktop notifications.
 */
export class DesktopNotificationStore {
  @observable accessor isSupported = false
  @observable accessor isEnabled = false
  @observable accessor isDenied = false

  constructor(currentUser$: Observable<User>) {
    this.show = this.show.bind(this)
    this.isSupported = !Notify.needsPermission || Notify.isSupported()

    if (this.isSupported) {
      this.isDenied = Notification.permission === 'denied'
    }

    // Restores the enabled state for each signed-in user.
    currentUser$.subscribe(() => {
      const stored = storage.getItem('desktopNotifications')
      const enabled = stored !== 'disabled'
      void this.setEnabled(enabled, stored !== null)
    })
  }

  /**
   * Enables or disables desktop notifications.
   *
   * @param enabled
   * @param noShowWelcome If `true`, does not show the welcome notification
   *  when enabled.
   */
  async setEnabled(enabled: boolean, noShowWelcome = false) {
    if (enabled) {
      if (this.isEnabled && !Notify.needsPermission) {
        return
      }
      enabled = await this._getPermission()
      runInAction(() => {
        this.isDenied = !enabled
      })
    }

    storage.setItem('desktopNotifications', enabled ? 'enabled' : 'disabled')
    runInAction(() => {
      this.isEnabled = enabled
    })

    if (this.isEnabled && !noShowWelcome) {
      this.show({
        title: 'Nice!',
        body: 'Desktop notifications have been enabled. You rock!',
        tag: 'notifications-enabled',
      })
    }
  }

  /**
   * Shows a desktop notification.
   *
   * @param opts
   */
  show(opts: SystemNotification) {
    if (!this.isEnabled) {
      return
    }

    const closeSubscription = (opts.close$ ?? NEVER).subscribe(() => {
      notification?.close()
    })

    const notification = new Notify(opts.title, {
      tag: opts.tag,
      body: stripMarkdown(opts.body),
      icon: opts.icon || DEFAULT_ICON,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- A zero timeout falls back to the default too.
      timeout: opts.timeout || 5,
      notifyError: () => {
        closeSubscription?.unsubscribe()
      },
      notifyClick: () => {
        window.focus()
        if (opts.onClick) {
          opts.onClick()
        }
        notification.close()
      },
      notifyClose: () => {
        closeSubscription?.unsubscribe()
      },
    })

    notification.show()
  }

  private _getPermission(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!Notify.needsPermission) {
        return resolve(true)
      }

      if (!this.isSupported) {
        return resolve(false)
      }

      Notify.requestPermission(
        () => resolve(true),
        () => resolve(false),
      )
    })
  }
}
